/**
 * PackagesAddons — ADDONS-2.
 *
 * 4th tab on /packages — merchant CRUD for per-item booking add-ons
 * (nail trim, take-home shampoo, late checkout). Cloned shape from
 * the Offerings tab; simpler since add-ons have no tier ladder.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { billing, type AddonService } from '../api/client'
import { Button } from '../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card'
import { AddonEditorDialog } from './AddonEditorDialog'

export function PackagesAddons() {
  const { t } = useTranslation()
  const [items, setItems] = useState<AddonService[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reload, setReload] = useState(0)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<AddonService | null>(null)

  useEffect(() => {
    let alive = true
    setItems(null)
    setError(null)
    billing
      .listAddOns({ active_only: false })
      .then((rows) => alive && setItems(rows))
      .catch(() => alive && setError('load_failed'))
    return () => {
      alive = false
    }
  }, [reload])

  function openCreate() {
    setEditing(null)
    setEditorOpen(true)
  }

  function openEdit(row: AddonService) {
    setEditing(row)
    setEditorOpen(true)
  }

  async function doDelete(row: AddonService) {
    if (!window.confirm(t('packages.addOns.deleteConfirm') as string)) return
    try {
      await billing.deleteAddOn(row.id)
      setReload((n) => n + 1)
    } catch (e) {
      setError((e as { detail?: string })?.detail ?? 'delete_failed')
    }
  }

  return (
    <div className="space-y-4" data-testid="packages-addons">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">
            {t('packages.addOns.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('packages.addOns.subtitle')}
          </p>
        </div>
        <Button onClick={openCreate} data-testid="addons-new">
          <Plus className="mr-1 h-4 w-4" />
          {t('packages.addOns.addBtn')}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t('packages.addOns.title')}
          </CardTitle>
          <CardDescription>
            {items
              ? `${items.length}`
              : t('packages.addOns.loading')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <p className="text-sm text-destructive">
              {t('packages.addOns.loadFailed')}
            </p>
          )}

          {items && items.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                {t('packages.addOns.empty')}
              </p>
              <Button
                onClick={openCreate}
                variant="outline"
                className="mt-3"
                data-testid="addons-empty-cta"
              >
                {t('packages.addOns.emptyCta')}
              </Button>
            </div>
          )}

          {items && items.length > 0 && (
            <div className="-mx-4 overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2">{t('packages.addOns.colName')}</th>
                    <th className="px-4 py-2 text-right">
                      {t('packages.addOns.colPrice')}
                    </th>
                    <th className="px-4 py-2">
                      {t('packages.addOns.colCategory')}
                    </th>
                    <th className="px-4 py-2">
                      {t('packages.addOns.colApplicableTo')}
                    </th>
                    <th className="px-4 py-2">
                      {t('packages.addOns.colStatus')}
                    </th>
                    <th className="px-4 py-2 text-right">
                      {t('packages.addOns.colActions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b last:border-0"
                      data-testid={`addon-row-${row.id}`}
                    >
                      <td className="px-4 py-2">
                        <div className="font-medium">{row.name_zh}</div>
                        <div className="text-xs text-muted-foreground">
                          {row.name}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        NT$ {row.price}
                      </td>
                      <td className="px-4 py-2">
                        <span className="rounded bg-muted px-2 py-0.5 text-xs">
                          {t(`packages.addOns.category.${row.category}`)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {row.applicable_service_types.map((s) => (
                            <span
                              key={s}
                              className="rounded border border-muted-foreground/30 px-1.5 py-0.5 text-xs text-muted-foreground"
                            >
                              {t(`packages.addOns.service.${s}`)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`rounded px-2 py-0.5 text-xs ${
                            row.is_active
                              ? 'bg-emerald-100 text-emerald-900'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {row.is_active
                            ? t('packages.addOns.statusActive')
                            : t('packages.addOns.statusInactive')}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(row)}
                            data-testid={`addon-edit-${row.id}`}
                          >
                            <Pencil className="mr-1 h-3 w-3" />
                            {t('packages.addOns.editBtn')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => doDelete(row)}
                            data-testid={`addon-delete-${row.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddonEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        existing={editing}
        onSaved={() => {
          setEditorOpen(false)
          setReload((n) => n + 1)
        }}
      />
    </div>
  )
}
